import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { updateClient, secureUpdateClientCode } from "../../../utils/api";

function onlyDigits(v) {
  return String(v ?? "").replace(/[^\d]/g, "");
}

function formatClientCode(client_number, branch_no) {
  if (!client_number) return "—";
  return branch_no ? `${client_number} ${branch_no}` : `${client_number}`;
}

function validateCritical(clientNumberStr, branchNoStr) {
  const cn = Number(clientNumberStr);
  if (!Number.isInteger(cn) || cn <= 0) return { ok: false, msg: "N° de cliente inválido." };

  const bnRaw = String(branchNoStr ?? "").trim();
  if (!bnRaw) {
    return { ok: true, client_number: cn, branch_no: null };
  }

  const bn = Number(bnRaw);
  if (!Number.isInteger(bn) || bn <= 0) return { ok: false, msg: "N° de sucursal inválido." };

  return { ok: true, client_number: cn, branch_no: bn };
}

export default function EditClientModal({ isOpen, onClose, client, onSaved }) {
  const [clientDb, setClientDb] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);

  const [form, setForm] = useState({
    name: "",
    cuit: "",
    tax_id: "",
    address: "",
    locality: "",  // ✅ NUEVO
    province: "",  // ✅ NUEVO
    contact: "",
    email: "",
    phone: "",
    branch_name: "",

    // ✅ emails de facturación
    billing_email_1: "",
    billing_email_2: "",
  });

  // Zona crítica
  const [criticalOpen, setCriticalOpen] = useState(false);
  const [criticalUnlock, setCriticalUnlock] = useState("");
  const [criticalReason, setCriticalReason] = useState("");
  const [criticalClientNumber, setCriticalClientNumber] = useState(""); // obligatorio
  const [criticalBranchNo, setCriticalBranchNo] = useState(""); // opcional -> NULL

  const parsedCritical = useMemo(
    () => validateCritical(criticalClientNumber, criticalBranchNo),
    [criticalClientNumber, criticalBranchNo]
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // ✅ 1) Al abrir el modal: traer registro fresco desde DB por id
  useEffect(() => {
    const id = client?.id;
    if (!isOpen || !id) return;

    let cancelled = false;

    const run = async () => {
      try {
        setLoadingClient(true);
        setErr(null);

        const { data, error } = await supabase
          .from("clients")
          .select(
            [
              "id",
              "name",
              "cuit",
              "tax_id",
              "address",
              "locality",     // ✅ NUEVO
              "province",     // ✅ NUEVO
              "contact",
              "email",
              "phone",
              "branch_name",
              "client_number",
              "branch_no",
              "billing_email_1",
              "billing_email_2",
            ].join(",")
          )
          .eq("id", id)
          .single();

        if (error) throw error;
        if (!cancelled) setClientDb(data);
      } catch (e) {
        // Si falla el fetch, usamos lo que vino en `client`
        if (!cancelled) setClientDb(null);
      } finally {
        if (!cancelled) setLoadingClient(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isOpen, client?.id]);

  // ✅ 2) Sincronizar formulario con clientDb (si existe) o con client (fallback)
  useEffect(() => {
    if (!isOpen) return;

    const src = clientDb || client;
    if (!src) return;

    setForm({
      name: src.name ?? "",
      cuit: src.cuit ?? "",
      tax_id: src.tax_id ?? "",
      address: src.address ?? "",
      locality: src.locality ?? "",   // ✅ NUEVO
      province: src.province ?? "",   // ✅ NUEVO
      contact: src.contact ?? "",
      email: src.email ?? "",
      phone: src.phone ?? "",
      branch_name: src.branch_name ?? "",

      // ✅ facturación
      billing_email_1: src.billing_email_1 ?? "",
      billing_email_2: src.billing_email_2 ?? "",
    });

    setCriticalOpen(false);
    setCriticalUnlock("");
    setCriticalReason("");
    setCriticalClientNumber(src.client_number ? String(src.client_number) : "");
    setCriticalBranchNo(src.branch_no ? String(src.branch_no) : "");
    setErr(null);
  }, [isOpen, clientDb, client]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const refetchClient = async () => {
    const id = client?.id;
    if (!id) return null;

    const { data, error } = await supabase
      .from("clients")
      .select(
        [
          "id",
          "name",
          "cuit",
          "tax_id",
          "address",
          "locality",   // ✅ NUEVO
          "province",   // ✅ NUEVO
          "contact",
          "email",
          "phone",
          "branch_name",
          "client_number",
          "branch_no",
          "billing_email_1",
          "billing_email_2",
        ].join(",")
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    setClientDb(data);
    return data;
  };

  const handleSave = async () => {
    if (!client?.id) return;

    // ✅ validación suave: si ambos están y son iguales
    const be1 = (form.billing_email_1 || "").trim();
    const be2 = (form.billing_email_2 || "").trim();
    if (be1 && be2 && be1.toLowerCase() === be2.toLowerCase()) {
      setErr("Los emails de facturación 1 y 2 no pueden ser iguales.");
      return;
    }

    try {
      setLoading(true);
      setErr(null);

      const payload = {
        ...form,

        // mantener consistencia CUIT/tax_id
        cuit: form.cuit || form.tax_id || null,
        tax_id: form.tax_id || form.cuit || null,

        branch_name: form.branch_name || null,

        // ✅ Dirección separada
        address: form.address || null,
        locality: form.locality || null, // ✅ NUEVO
        province: form.province || null, // ✅ NUEVO

        contact: form.contact || null,
        email: form.email || null,
        phone: form.phone || null,

        // ✅ facturación
        billing_email_1: be1 || null,
        billing_email_2: be2 || null,

        name: (form.name || "").trim(),
      };

      if (!payload.name) {
        setErr("El nombre / razón social es obligatorio");
        return;
      }

      await updateClient(client.id, payload);

      // ✅ Volver a traer desde DB para que al reabrir SIEMPRE muestre lo guardado
      const fresh = await refetchClient();
      onSaved?.(fresh ?? { ...client, ...payload });
    } catch (e) {
      setErr(e?.message || "Error guardando cliente");
    } finally {
      setLoading(false);
    }
  };

  const handleCriticalSave = async () => {
    if (!client?.id) return;

    if (String(criticalUnlock).trim().toUpperCase() !== "CAMBIAR") {
      setErr('Para cambiar el número/sucursal, escribí "CAMBIAR" en el campo de confirmación.');
      return;
    }

    if (!parsedCritical.ok) {
      setErr(parsedCritical.msg || "Datos inválidos.");
      return;
    }

    if (!criticalReason.trim()) {
      setErr("Escribí el motivo del cambio (para auditoría interna).");
      return;
    }

    try {
      setLoading(true);
      setErr(null);

      await secureUpdateClientCode({
        client_id: client.id,
        new_client_number: parsedCritical.client_number,
        new_branch_no: parsedCritical.branch_no,
        reason: criticalReason.trim(),
      });

      const fresh = await refetchClient();
      onSaved?.(fresh ?? client);
    } catch (e) {
      setErr(e?.message || "Error en cambio crítico.");
    } finally {
      setLoading(false);
    }
  };

  const srcHeader = clientDb || client;
  const currentCode = formatClientCode(srcHeader?.client_number, srcHeader?.branch_no);

  const previewCode = parsedCritical.ok
    ? formatClientCode(parsedCritical.client_number, parsedCritical.branch_no)
    : "—";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 px-4">
      {/* ✅ Modal con alto máximo + scroll interno + footer fijo */}
      <div className="w-full max-w-2xl max-h-[calc(100vh-2rem)] rounded-2xl bg-[#0F1115] text-white border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Header fijo */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">Editar cliente</div>
            <div className="text-xs text-slate-300">
              {currentCode} — {srcHeader?.name ?? ""}
              {loadingClient ? <span className="ml-2 text-slate-400">(cargando...)</span> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-300 hover:text-white"
            disabled={loading || loadingClient}
          >
            Cerrar
          </button>
        </div>

        {/* Body scrolleable */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          {err && (
            <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/50 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          {/* Campos normales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Razón social *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Sucursal / Planta</label>
              <input
                name="branch_name"
                value={form.branch_name}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">CUIT</label>
              <input
                name="cuit"
                value={form.cuit}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tax ID (si lo usás)</label>
              <input
                name="tax_id"
                value={form.tax_id}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Dirección (calle y número)</label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            {/* ✅ NUEVO: Localidad / Provincia */}
            <div>
              <label className="block text-sm font-medium mb-1">Localidad</label>
              <input
                name="locality"
                value={form.locality}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                placeholder="Ej: Rivadavia"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Provincia</label>
              <input
                name="province"
                value={form.province}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                placeholder="Ej: San Juan"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Contacto</label>
              <input
                name="contact"
                value={form.contact}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>
          </div>

          {/* ✅ Emails de facturación */}
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="font-semibold text-sm">Emails de facturación</div>
            <div className="text-xs text-slate-300 mt-1">
              Se usan para enviar facturas/OC/remitos (distinto al email de contacto).
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Facturación 1</label>
                <input
                  name="billing_email_1"
                  type="email"
                  value={form.billing_email_1}
                  onChange={handleChange}
                  className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  placeholder="facturacion@cliente.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Facturación 2</label>
                <input
                  name="billing_email_2"
                  type="email"
                  value={form.billing_email_2}
                  onChange={handleChange}
                  className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  placeholder="cuentasapagar@cliente.com"
                />
              </div>
            </div>
          </div>

          {/* Zona crítica */}
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-sm">Cambiar número / sucursal (zona crítica)</div>
                <div className="text-xs text-slate-300">
                  Solo para correcciones cuando Facturación no puede cambiar el número.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCriticalOpen((s) => !s)}
                className="px-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-sm"
              >
                {criticalOpen ? "Ocultar" : "Desbloquear"}
              </button>
            </div>

            {criticalOpen && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Nuevo N° de cliente *</label>
                  <input
                    value={criticalClientNumber}
                    onChange={(e) => setCriticalClientNumber(onlyDigits(e.target.value))}
                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    placeholder='Ej: "1088"'
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nuevo N° de sucursal (opcional)</label>
                  <input
                    value={criticalBranchNo}
                    onChange={(e) => setCriticalBranchNo(onlyDigits(e.target.value))}
                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    placeholder='Ej: "2" (vacío = sin sucursal)'
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="text-xs text-slate-300">
                    {parsedCritical.ok ? (
                      <>
                        Se aplicará: <span className="text-white font-semibold">{previewCode}</span>
                        {!criticalBranchNo.trim() ? (
                          <span className="ml-2 text-slate-400">(sin sucursal → branch_no NULL)</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-red-300">{parsedCritical.msg || "Datos inválidos"}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Confirmación (escribí CAMBIAR)</label>
                  <input
                    value={criticalUnlock}
                    onChange={(e) => setCriticalUnlock(e.target.value)}
                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    placeholder="CAMBIAR"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Motivo del cambio</label>
                  <input
                    value={criticalReason}
                    onChange={(e) => setCriticalReason(e.target.value)}
                    className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    placeholder="Ej: Corrección para coincidir con sistema de facturación"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleCriticalSave}
                    disabled={loading || loadingClient}
                    className="px-4 py-2 rounded-md bg-amber-600 text-sm font-medium hover:bg-amber-500 disabled:opacity-60"
                  >
                    Aplicar cambio crítico
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer fijo */}
        <div className="border-t border-white/10 bg-[#0F1115]/90 backdrop-blur px-5 py-4">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-white/20 bg-white/5 text-sm hover:bg-white/10 disabled:opacity-60"
              disabled={loading || loadingClient}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 rounded-md bg-emerald-600 text-sm font-medium hover:bg-emerald-500 disabled:opacity-60"
              disabled={loading || loadingClient}
            >
              {loading ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
